// traceability: requirements: [NFR-013]
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button } from './ui/Button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }

  isChunkLoadError(error: Error | null): boolean {
    if (!error?.message) return false;
    return (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.name === 'ChunkLoadError'
    );
  }

  handleReset = () => {
    if (this.isChunkLoadError(this.state.error)) {
      // Stale deploy: reload so the browser picks up the new index + asset hashes
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const chunkError = this.isChunkLoadError(this.state.error);

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8 text-center" role="alert">
          <div className="rounded-full bg-cinnabar-100 p-4 dark:bg-cinnabar-900/30">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-cinnabar-600 dark:text-cinnabar-400">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-50">Something went wrong</h2>
          <p className="max-w-md text-sm text-zinc-500 dark:text-zinc-400">
            {chunkError
              ? 'The app was updated. Click Try Again to reload the latest version.'
              : 'An unexpected error occurred. Please try again or contact support if the problem persists.'}
          </p>
          {this.state.error && (
            <pre className="mt-2 max-w-lg overflow-auto rounded-lg bg-zinc-100 p-3 text-xs text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
              {this.state.error.message}
            </pre>
          )}
          <Button onClick={this.handleReset} variant="primary">
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}
